import { ButtonType } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import theme from 'shared/styles/theme';

import CalendarPicker from './CalendarPicker';

interface CalendarModalPickerProps {
  calendarModalVisible: boolean;
  getSelectedDate: (day: string) => void;
  userSelectedDate: string;
  minDate?: string;
  maxDate?: string;
  pastScrollRange?: number;
  futureScrollRange?: number;
  viewedDate?: string;
}

const CalendarModalPicker = ({
  calendarModalVisible,
  getSelectedDate,
  userSelectedDate,
  minDate,
  maxDate,
  pastScrollRange,
  futureScrollRange,
  viewedDate,
}: CalendarModalPickerProps) => {
  const { t: translatedText } = useTranslation('calendar', {
    keyPrefix: 'calendar',
  });
  const [modalVisible, setModalVisible] = useState(calendarModalVisible);
  const [selectedDate, setSelectedDate] = useState<string>(userSelectedDate);

  const handleOk = () => {
    setModalVisible(false);
    getSelectedDate(selectedDate);
  };

  const handleCancel = () => {
    // Handle Cancel button action
    setModalVisible(false);
    getSelectedDate(userSelectedDate);
  };

  const handleDayPress = (date: string) => {
    setSelectedDate(date);
  };

  return (
    <View style={styles.container}>
      <Modal
        transparent
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalBackground}>
          <View style={styles.modalContainer}>
            <View style={styles.calendar}>
              <CalendarPicker
                futureScrollRange={futureScrollRange ?? 2}
                calendarWidth={280}
                pastScrollRange={pastScrollRange ?? 0}
                minDate={minDate === '' ? undefined : new Date().toDateString()}
                maxDate={maxDate}
                onDateListener={(date) => handleDayPress(date)}
                userSelectedDate={selectedDate}
                viewedDate={viewedDate}
              />
            </View>
            <View style={styles.bottomStyle}>
              <View style={styles.dividerWrapper}>
                <HairLineDivider />
              </View>
              <View style={styles.buttonContainer}>
                <View style={styles.buttonStyle}>
                  <Button
                    label={translatedText('buttons.ok')}
                    onPress={handleOk}
                    type={ButtonType.Primary}
                  />
                </View>
                <View style={styles.buttonStyle}>
                  <Button
                    label={translatedText('buttons.cancel')}
                    onPress={handleCancel}
                    type={ButtonType.Primary}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomStyle: {},
  buttonContainer: {
    flexDirection: 'row',
    gap: 20,
    justifyContent: 'space-around',
    padding: 20,
    width: '100%',
  },
  buttonStyle: {
    flex: 1,
  },
  calendar: {
    flex: 1,
    padding: 20,
  },
  container: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  modalBackground: {
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    flex: 1,
    justifyContent: 'center', // Semi-transparent background
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 15,
    height: 500,
    width: 320,
  },
});

export default CalendarModalPicker;
